/**
 * Admin — Research Explorer (Phase 3.2)
 *
 * Single tabbed surface for all Vaccine Explorer authoring:
 *   • Vaccines        — list + create + inline edit
 *   • Research Entities — manufacturers / ingredients / diseases / surveillance
 *   • Schedules       — per-vaccine UK / US tables
 *   • Evidence Blocks — per-vaccine safety & evidence sections
 *   • Relationships   — corporate / domain entity-to-entity graph
 *   • CSV Import      — bulk seed schedules from a textarea
 *
 * This is intentionally one page rather than five separate routes — the
 * editorial workflow is "pick a vaccine, then enrich it" and we don't want
 * to scatter that across the app. Future explorers will instantiate a
 * thin wrapper that reuses the same components.
 */
import React, { useEffect, useMemo, useState } from "react";
import AdminLayout from "./AdminLayout";
import api from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Save, Trash2, ChevronRight, Upload, Link as LinkIcon, Tag } from "lucide-react";

const ENTITY_TYPES = ["manufacturer", "ingredient", "disease", "surveillance_system"];
const COUNTRIES = ["UK", "US"];
const BLOCK_TYPES = [
    "official_information", "official_side_effects", "safety_monitoring",
    "published_research", "research_area", "historical_documentation",
    "regulatory_history", "manufacturing", "clinical_trials",
    "ingredients_excipients", "liability_compensation",
    "international_differences", "policy_changes",
];
const EVIDENCE_LABELS = [
    "official_guidance", "regulatory_document", "peer_reviewed",
    "safety_surveillance", "historical", "tcp_investigation",
];
const EVIDENCE_GRADES = ["established", "conflicting", "ongoing_research", "hypothesis"];
const RELATIONSHIP_TYPES = [
    "parent_of", "subsidiary_of", "acquired", "divested", "merged_with",
    "partnered_with", "joint_venture_with", "licensed_from", "licensed_to",
    "manufactures", "regulates", "approved_by", "monitored_by",
    "targets_disease", "contains_ingredient", "studied_in", "funded_by", "other",
];

// ---------- small reusable atoms ----------

function Input({ label, value, onChange, placeholder, type = "text", testId, required = false }) {
    return (
        <label className="block">
            <span className="text-[10px] uppercase tracking-[0.22em] text-[#64748B] mb-1.5 block">{label}</span>
            <input
                data-testid={testId}
                type={type}
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                required={required}
                className="w-full bg-[#0B1220] border border-[#243044] rounded-md px-3 py-2 text-sm text-white placeholder:text-[#475569] focus:outline-none focus:border-[#56CCF2]/60"
            />
        </label>
    );
}

function Select({ label, value, onChange, options, testId }) {
    return (
        <label className="block">
            <span className="text-[10px] uppercase tracking-[0.22em] text-[#64748B] mb-1.5 block">{label}</span>
            <select
                data-testid={testId}
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                className="w-full bg-[#0B1220] border border-[#243044] rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-[#56CCF2]/60"
            >
                {options.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                ))}
            </select>
        </label>
    );
}

function Textarea({ label, value, onChange, rows = 4, testId, placeholder }) {
    return (
        <label className="block">
            <span className="text-[10px] uppercase tracking-[0.22em] text-[#64748B] mb-1.5 block">{label}</span>
            <textarea
                data-testid={testId}
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                rows={rows}
                placeholder={placeholder}
                className="w-full bg-[#0B1220] border border-[#243044] rounded-md px-3 py-2 text-sm text-white placeholder:text-[#475569] focus:outline-none focus:border-[#56CCF2]/60 font-mono"
            />
        </label>
    );
}

function Btn({ children, onClick, variant = "primary", testId, disabled, type = "button" }) {
    const base = "inline-flex items-center gap-2 px-3.5 py-2 rounded-md text-xs font-medium tracking-wide transition-colors disabled:opacity-50";
    const styles = {
        primary: "bg-[#2F80ED] hover:bg-[#1E6FE0] text-white",
        ghost: "bg-[#111827] hover:bg-[#1a2233] text-[#94A3B8] border border-[#243044]",
        danger: "bg-[#4a1f1f] hover:bg-[#5a2525] text-[#F87171] border border-[#5a2525]",
    };
    return (
        <button type={type} onClick={onClick} disabled={disabled} data-testid={testId} className={`${base} ${styles[variant]}`}>
            {children}
        </button>
    );
}

function SectionHeader({ title, subtitle, action }) {
    return (
        <div className="flex items-end justify-between mb-5 border-b border-[#243044] pb-3">
            <div>
                <h2 className="text-lg font-bold text-white tracking-tight">{title}</h2>
                {subtitle ? <p className="text-xs text-[#64748B] mt-1">{subtitle}</p> : null}
            </div>
            {action}
        </div>
    );
}

// ---------- tab: Vaccines ----------

function VaccinesTab({ vaccines, refresh, researchEntities }) {
    const [editing, setEditing] = useState(null); // vaccine doc or { __new: true }
    const empty = {
        __new: true,
        slug: "", display_name: "", aka: [], antigens: [], description_md: "",
        manufacturer_entity_ids: [], ingredient_entity_ids: [], disease_target_entity_ids: [],
        official_sources: [], status: "draft", indexable: true,
    };

    const save = async (v) => {
        try {
            if (v.__new) {
                await api.post("/admin/vaccines", {
                    slug: v.slug, display_name: v.display_name, aka: v.aka, antigens: v.antigens,
                    description_md: v.description_md, manufacturer_entity_ids: v.manufacturer_entity_ids,
                    ingredient_entity_ids: v.ingredient_entity_ids, disease_target_entity_ids: v.disease_target_entity_ids,
                    official_sources: v.official_sources, status: v.status, indexable: v.indexable,
                });
                toast.success("Vaccine created.");
            } else {
                const { id, created_at, updated_at, __new, ...rest } = v;
                await api.patch(`/admin/vaccines/${id}`, rest);
                toast.success("Vaccine updated.");
            }
            setEditing(null);
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed.");
        }
    };

    const del = async (id) => {
        if (!window.confirm("Delete vaccine and ALL its schedules / evidence blocks?")) return;
        try {
            await api.delete(`/admin/vaccines/${id}`);
            toast.success("Vaccine deleted.");
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed.");
        }
    };

    if (editing) {
        return <VaccineEditor v={editing} onCancel={() => setEditing(null)} onSave={save} researchEntities={researchEntities} />;
    }

    return (
        <div>
            <SectionHeader
                title="Vaccines"
                subtitle="Factual records. Editorial content lives in evidence blocks attached to each vaccine."
                action={<Btn testId="vaccine-new" onClick={() => setEditing(empty)}><Plus className="w-3.5 h-3.5" /> New vaccine</Btn>}
            />
            {vaccines.length === 0 ? (
                <div className="text-[13px] text-[#64748B] py-12 text-center">No vaccines yet. Create the first one to begin authoring.</div>
            ) : (
                <div className="space-y-2">
                    {vaccines.map((v) => (
                        <div key={v.id} className="flex items-center justify-between bg-[#111827] border border-[#243044] rounded-md px-4 py-3 hover:border-[#56CCF2]/40 transition-colors" data-testid={`vaccine-row-${v.slug}`}>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 text-sm text-white truncate">
                                    {v.display_name}
                                    <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ${v.status === "published" ? "bg-[#1f4a2d] text-[#7FE0A6]" : "bg-[#3a2b1a] text-[#E0B341]"}`}>
                                        {v.status}
                                    </span>
                                </div>
                                <div className="text-[11px] text-[#64748B] font-mono mt-0.5">/vaccines/v/{v.slug}</div>
                            </div>
                            <div className="flex items-center gap-2">
                                <Btn variant="ghost" testId={`vaccine-edit-${v.slug}`} onClick={() => setEditing(v)}>Edit</Btn>
                                <Btn variant="danger" testId={`vaccine-delete-${v.slug}`} onClick={() => del(v.id)}><Trash2 className="w-3.5 h-3.5" /></Btn>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function VaccineEditor({ v, onCancel, onSave, researchEntities }) {
    const [draft, setDraft] = useState({ ...v });
    const upd = (k) => (val) => setDraft((d) => ({ ...d, [k]: val }));
    const updArr = (k) => (val) => setDraft((d) => ({ ...d, [k]: val.split("\n").map((s) => s.trim()).filter(Boolean) }));

    const byType = (t) => researchEntities.filter((e) => e.entity_type === t);

    const toggleMulti = (key, id) => {
        const cur = new Set(draft[key] || []);
        if (cur.has(id)) cur.delete(id); else cur.add(id);
        upd(key)([...cur]);
    };

    return (
        <div>
            <SectionHeader
                title={v.__new ? "New vaccine" : `Edit: ${v.display_name}`}
                subtitle="Schedules + evidence blocks are managed in the tabs above after saving."
                action={
                    <div className="flex items-center gap-2">
                        <Btn variant="ghost" testId="vaccine-cancel" onClick={onCancel}>Cancel</Btn>
                        <Btn testId="vaccine-save" onClick={() => onSave(draft)}><Save className="w-3.5 h-3.5" /> Save</Btn>
                    </div>
                }
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Input label="Slug (URL-safe)" value={draft.slug} onChange={upd("slug")} placeholder="mmr" testId="vaccine-slug" required />
                <Input label="Display name" value={draft.display_name} onChange={upd("display_name")} placeholder="MMR (Measles, Mumps, Rubella)" testId="vaccine-display-name" required />
                <Textarea label="Also known as (one per line)" value={(draft.aka || []).join("\n")} onChange={updArr("aka")} rows={3} placeholder="MMRII&#10;Priorix" testId="vaccine-aka" />
                <Textarea label="Antigens (one per line)" value={(draft.antigens || []).join("\n")} onChange={updArr("antigens")} rows={3} placeholder="measles&#10;mumps&#10;rubella" testId="vaccine-antigens" />
                <div className="md:col-span-2">
                    <Textarea label="Description (markdown)" value={draft.description_md} onChange={upd("description_md")} rows={5} testId="vaccine-description" />
                </div>
                <Select label="Status" value={draft.status} onChange={upd("status")} options={[{ value: "draft", label: "Draft" }, { value: "published", label: "Published" }]} testId="vaccine-status" />
                <Input label="Last reviewed (ISO date)" value={draft.last_reviewed_at} onChange={upd("last_reviewed_at")} placeholder="2026-06-20" testId="vaccine-last-reviewed" />

                {/* Graph cross-links */}
                <EntityPicker label="Manufacturers" entities={byType("manufacturer")} selected={draft.manufacturer_entity_ids} onToggle={(id) => toggleMulti("manufacturer_entity_ids", id)} testId="vaccine-pick-manufacturer" />
                <EntityPicker label="Ingredients" entities={byType("ingredient")} selected={draft.ingredient_entity_ids} onToggle={(id) => toggleMulti("ingredient_entity_ids", id)} testId="vaccine-pick-ingredient" />
                <EntityPicker label="Disease(s) targeted" entities={byType("disease")} selected={draft.disease_target_entity_ids} onToggle={(id) => toggleMulti("disease_target_entity_ids", id)} testId="vaccine-pick-disease" />
            </div>
        </div>
    );
}

function EntityPicker({ label, entities, selected, onToggle, testId }) {
    const [filter, setFilter] = useState("");
    const visible = entities.filter((e) => (e.name || "").toLowerCase().includes(filter.toLowerCase()));
    return (
        <div className="bg-[#0B1220] border border-[#243044] rounded-md p-3" data-testid={testId}>
            <div className="flex items-center gap-2 mb-2">
                <LinkIcon className="w-3 h-3 text-[#56CCF2]" />
                <span className="text-[10px] uppercase tracking-[0.22em] text-[#64748B]">{label}</span>
                <span className="text-[10px] text-[#64748B] ml-auto">{(selected || []).length} selected</span>
            </div>
            <input
                type="text"
                placeholder={`Filter ${label.toLowerCase()}…`}
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="w-full bg-[#111827] border border-[#243044] rounded px-2 py-1 text-xs text-white placeholder:text-[#475569] focus:outline-none focus:border-[#56CCF2]/50 mb-2"
            />
            <div className="max-h-44 overflow-y-auto space-y-1">
                {visible.length === 0 ? (
                    <div className="text-[11px] text-[#64748B] py-2 text-center italic">No entities of this type yet. Create them in the Research Entities tab.</div>
                ) : visible.map((e) => {
                    const isSel = (selected || []).includes(e.id);
                    return (
                        <button key={e.id} type="button" onClick={() => onToggle(e.id)} className={`w-full text-left px-2 py-1.5 rounded text-xs transition-colors ${isSel ? "bg-[#1e3a5f] text-white" : "hover:bg-[#111827] text-[#94A3B8]"}`}>
                            <span className="inline-block w-3 mr-1.5">{isSel ? "✓" : ""}</span>
                            {e.name}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

// ---------- tab: Research Entities ----------

function ResearchEntitiesTab({ entities, refresh }) {
    const [filter, setFilter] = useState("manufacturer");
    const [creating, setCreating] = useState(null);

    const empty = (type) => ({
        __new: true, entity_type: type, name: "", slug: "", aka: [], summary_md: "",
        official_url: "", country: "", status: "published", metadata: {},
    });

    const save = async (e) => {
        try {
            if (e.__new) {
                await api.post("/admin/research-entities", e);
                toast.success("Entity created.");
            } else {
                const { id, entity_type, created_at, updated_at, __new, ...rest } = e;
                await api.patch(`/admin/research-entities/${id}`, rest);
                toast.success("Entity updated.");
            }
            setCreating(null);
            refresh();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed.");
        }
    };
    const del = async (id) => {
        if (!window.confirm("Delete entity? Its relationships will also be removed.")) return;
        try {
            await api.delete(`/admin/research-entities/${id}`);
            toast.success("Entity deleted.");
            refresh();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Delete failed.");
        }
    };

    const filtered = entities.filter((e) => e.entity_type === filter);

    if (creating) {
        return <ResearchEntityEditor e={creating} onCancel={() => setCreating(null)} onSave={save} />;
    }

    return (
        <div>
            <SectionHeader
                title="Research Entities"
                subtitle="Manufacturers, ingredients, diseases and surveillance systems — reusable across every TCP explorer."
                action={<Btn testId="entity-new" onClick={() => setCreating(empty(filter))}><Plus className="w-3.5 h-3.5" /> New {filter.replace("_", " ")}</Btn>}
            />
            <div className="flex gap-2 mb-5">
                {ENTITY_TYPES.map((t) => (
                    <button
                        key={t}
                        onClick={() => setFilter(t)}
                        data-testid={`entity-filter-${t}`}
                        className={`px-3 py-1.5 rounded-md text-xs transition-colors ${filter === t ? "bg-[#2F80ED] text-white" : "bg-[#111827] text-[#94A3B8] hover:text-white border border-[#243044]"}`}
                    >
                        {t.replace("_", " ")} ({entities.filter((e) => e.entity_type === t).length})
                    </button>
                ))}
            </div>
            {filtered.length === 0 ? (
                <div className="text-[13px] text-[#64748B] py-12 text-center">No {filter.replace("_", " ")} entities yet.</div>
            ) : (
                <div className="space-y-2">
                    {filtered.map((e) => (
                        <div key={e.id} className="flex items-center justify-between bg-[#111827] border border-[#243044] rounded-md px-4 py-3" data-testid={`entity-row-${e.slug}`}>
                            <div className="min-w-0 flex-1">
                                <div className="text-sm text-white truncate">{e.name}</div>
                                <div className="text-[11px] text-[#64748B] font-mono mt-0.5">/{e.slug}{e.country ? ` · ${e.country}` : ""}</div>
                            </div>
                            <div className="flex items-center gap-2">
                                <Btn variant="ghost" testId={`entity-edit-${e.slug}`} onClick={() => setCreating(e)}>Edit</Btn>
                                <Btn variant="danger" testId={`entity-delete-${e.slug}`} onClick={() => del(e.id)}><Trash2 className="w-3.5 h-3.5" /></Btn>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function ResearchEntityEditor({ e, onCancel, onSave }) {
    const [draft, setDraft] = useState({ ...e });
    const upd = (k) => (val) => setDraft((d) => ({ ...d, [k]: val }));
    const updArr = (k) => (val) => setDraft((d) => ({ ...d, [k]: val.split("\n").map((s) => s.trim()).filter(Boolean) }));
    return (
        <div>
            <SectionHeader
                title={e.__new ? `New ${e.entity_type.replace("_", " ")}` : `Edit: ${e.name}`}
                action={
                    <div className="flex items-center gap-2">
                        <Btn variant="ghost" onClick={onCancel}>Cancel</Btn>
                        <Btn testId="entity-save" onClick={() => onSave(draft)}><Save className="w-3.5 h-3.5" /> Save</Btn>
                    </div>
                }
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Input label="Name" value={draft.name} onChange={upd("name")} testId="entity-name" required />
                <Input label="Slug" value={draft.slug} onChange={upd("slug")} testId="entity-slug" required placeholder="gsk" />
                <Textarea label="Aliases (one per line)" value={(draft.aka || []).join("\n")} onChange={updArr("aka")} rows={3} />
                <Textarea label="Summary (markdown)" value={draft.summary_md} onChange={upd("summary_md")} rows={3} />
                <Input label="Official URL" value={draft.official_url} onChange={upd("official_url")} />
                <Input label="Country" value={draft.country} onChange={upd("country")} placeholder="UK / US / GLOBAL" />
                <Select label="Status" value={draft.status} onChange={upd("status")} options={[{ value: "draft", label: "Draft" }, { value: "published", label: "Published" }]} />
            </div>
        </div>
    );
}

// ---------- tab: Schedules + Evidence Blocks + Relationships ----------

function SchedulesTab({ vaccines, refresh }) {
    const [vid, setVid] = useState("");
    const [rows, setRows] = useState([]);
    const [busy, setBusy] = useState(false);
    const [creating, setCreating] = useState(false);
    const empty = { country: "UK", age_label: "", age_months: 0, dose_number: 1, total_doses: 1, schedule_year: new Date().getFullYear(), status: "published", source_url: "", notes_md: "" };
    const [draft, setDraft] = useState(empty);

    useEffect(() => {
        if (!vid) { setRows([]); return; }
        setBusy(true);
        api.get(`/admin/vaccines/${vid}/schedules`).then((r) => setRows(r.data)).finally(() => setBusy(false));
    }, [vid]);

    const create = async () => {
        try {
            await api.post("/admin/vaccine-schedules", { vaccine_id: vid, ...draft, age_months: Number(draft.age_months), dose_number: Number(draft.dose_number), total_doses: Number(draft.total_doses), schedule_year: Number(draft.schedule_year) });
            toast.success("Schedule row added.");
            setCreating(false); setDraft(empty);
            const r = await api.get(`/admin/vaccines/${vid}/schedules`); setRows(r.data);
        } catch (e) { toast.error(e?.response?.data?.detail || "Add failed."); }
    };
    const del = async (id) => {
        try { await api.delete(`/admin/vaccine-schedules/${id}`); setRows((r) => r.filter((x) => x.id !== id)); toast.success("Row deleted."); } catch (e) { toast.error("Delete failed."); }
    };

    return (
        <div>
            <SectionHeader title="Schedules" subtitle="Per-country, per-age rows. One row per (vaccine, country, age, dose)." />
            <Select label="Vaccine" value={vid} onChange={setVid} options={[{ value: "", label: "Choose a vaccine…" }, ...vaccines.map((v) => ({ value: v.id, label: v.display_name }))]} testId="schedule-vaccine-select" />
            {vid ? (
                <div className="mt-6">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin text-[#56CCF2]" /> : null}
                    <div className="mt-4 space-y-2">
                        {rows.map((s) => (
                            <div key={s.id} className="flex items-center justify-between bg-[#111827] border border-[#243044] rounded-md px-4 py-2" data-testid={`schedule-row-${s.id}`}>
                                <div className="text-sm text-white">
                                    <span className="text-[#56CCF2]">{s.country}</span> · {s.age_label} · dose {s.dose_number}/{s.total_doses} · {s.schedule_year}
                                </div>
                                <Btn variant="danger" onClick={() => del(s.id)}><Trash2 className="w-3.5 h-3.5" /></Btn>
                            </div>
                        ))}
                    </div>
                    {creating ? (
                        <div className="mt-5 bg-[#0B1220] border border-[#56CCF2]/30 rounded-md p-4">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <Select label="Country" value={draft.country} onChange={(v) => setDraft({ ...draft, country: v })} options={COUNTRIES.map((c) => ({ value: c, label: c }))} testId="schedule-new-country" />
                                <Input label="Age label" value={draft.age_label} onChange={(v) => setDraft({ ...draft, age_label: v })} placeholder="12 months" testId="schedule-new-age-label" />
                                <Input label="Age (months)" value={draft.age_months} onChange={(v) => setDraft({ ...draft, age_months: v })} type="number" testId="schedule-new-age-months" />
                                <Input label="Year" value={draft.schedule_year} onChange={(v) => setDraft({ ...draft, schedule_year: v })} type="number" />
                                <Input label="Dose #" value={draft.dose_number} onChange={(v) => setDraft({ ...draft, dose_number: v })} type="number" />
                                <Input label="Of total" value={draft.total_doses} onChange={(v) => setDraft({ ...draft, total_doses: v })} type="number" />
                                <Input label="Source URL" value={draft.source_url} onChange={(v) => setDraft({ ...draft, source_url: v })} />
                                <Select label="Status" value={draft.status} onChange={(v) => setDraft({ ...draft, status: v })} options={[{ value: "draft", label: "Draft" }, { value: "published", label: "Published" }]} />
                            </div>
                            <div className="mt-3 flex justify-end gap-2">
                                <Btn variant="ghost" onClick={() => { setCreating(false); setDraft(empty); }}>Cancel</Btn>
                                <Btn testId="schedule-save" onClick={create}><Save className="w-3.5 h-3.5" /> Add row</Btn>
                            </div>
                        </div>
                    ) : (
                        <div className="mt-4"><Btn testId="schedule-add" onClick={() => setCreating(true)}><Plus className="w-3.5 h-3.5" /> Add schedule row</Btn></div>
                    )}
                </div>
            ) : null}
        </div>
    );
}

function EvidenceBlocksTab({ vaccines }) {
    const [vid, setVid] = useState("");
    const [blocks, setBlocks] = useState([]);
    const [creating, setCreating] = useState(false);
    const empty = { block_type: "official_side_effects", evidence_label: "official_guidance", evidence_grade: "", title: "", body_md: "", citations: [], order: 0, status: "published" };
    const [draft, setDraft] = useState(empty);

    useEffect(() => {
        if (!vid) { setBlocks([]); return; }
        api.get("/admin/evidence-blocks", { params: { target_kind: "vaccine", target_id: vid } }).then((r) => setBlocks(r.data));
    }, [vid]);

    const create = async () => {
        try {
            const payload = { target_kind: "vaccine", target_id: vid, ...draft, order: Number(draft.order) };
            if (!payload.evidence_grade) delete payload.evidence_grade;
            await api.post("/admin/evidence-blocks", payload);
            toast.success("Block added.");
            setCreating(false); setDraft(empty);
            const r = await api.get("/admin/evidence-blocks", { params: { target_kind: "vaccine", target_id: vid } });
            setBlocks(r.data);
        } catch (e) { toast.error(e?.response?.data?.detail || "Add failed."); }
    };
    const del = async (id) => {
        try { await api.delete(`/admin/evidence-blocks/${id}`); setBlocks((b) => b.filter((x) => x.id !== id)); toast.success("Block deleted."); } catch (e) { toast.error("Delete failed."); }
    };

    return (
        <div>
            <SectionHeader title="Evidence Blocks" subtitle="Reusable across all explorers. Research-area blocks require an evidence grade." />
            <Select label="Vaccine" value={vid} onChange={setVid} options={[{ value: "", label: "Choose a vaccine…" }, ...vaccines.map((v) => ({ value: v.id, label: v.display_name }))]} testId="evidence-vaccine-select" />
            {vid ? (
                <div className="mt-6">
                    <div className="space-y-2">
                        {blocks.map((b) => (
                            <div key={b.id} className="bg-[#111827] border border-[#243044] rounded-md px-4 py-3" data-testid={`evidence-row-${b.id}`}>
                                <div className="flex items-start justify-between">
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#1e3a5f] text-[#56CCF2]">{b.block_type.replace(/_/g, " ")}</span>
                                            <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#2a1f3a] text-[#A78BFA]">{b.evidence_label.replace(/_/g, " ")}</span>
                                            {b.evidence_grade ? <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#3a2b1a] text-[#E0B341]">{b.evidence_grade.replace(/_/g, " ")}</span> : null}
                                        </div>
                                        <div className="text-sm text-white">{b.title}</div>
                                        {b.body_md ? <div className="text-[12px] text-[#64748B] mt-1 line-clamp-2">{b.body_md.slice(0, 200)}</div> : null}
                                    </div>
                                    <Btn variant="danger" onClick={() => del(b.id)}><Trash2 className="w-3.5 h-3.5" /></Btn>
                                </div>
                            </div>
                        ))}
                    </div>
                    {creating ? (
                        <div className="mt-5 bg-[#0B1220] border border-[#56CCF2]/30 rounded-md p-4 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <Select label="Block type" value={draft.block_type} onChange={(v) => setDraft({ ...draft, block_type: v })} options={BLOCK_TYPES.map((t) => ({ value: t, label: t.replace(/_/g, " ") }))} testId="evidence-new-block-type" />
                                <Select label="Evidence label" value={draft.evidence_label} onChange={(v) => setDraft({ ...draft, evidence_label: v })} options={EVIDENCE_LABELS.map((t) => ({ value: t, label: t.replace(/_/g, " ") }))} testId="evidence-new-label" />
                                <Select label={draft.block_type === "research_area" ? "Evidence grade (REQUIRED)" : "Evidence grade (optional)"} value={draft.evidence_grade} onChange={(v) => setDraft({ ...draft, evidence_grade: v })} options={[{ value: "", label: "—" }, ...EVIDENCE_GRADES.map((g) => ({ value: g, label: g.replace(/_/g, " ") }))]} testId="evidence-new-grade" />
                            </div>
                            <Input label="Title" value={draft.title} onChange={(v) => setDraft({ ...draft, title: v })} testId="evidence-new-title" required />
                            <Textarea label="Body (markdown)" value={draft.body_md} onChange={(v) => setDraft({ ...draft, body_md: v })} rows={8} testId="evidence-new-body" />
                            <div className="grid grid-cols-2 gap-4">
                                <Input label="Display order" value={draft.order} onChange={(v) => setDraft({ ...draft, order: v })} type="number" />
                                <Select label="Status" value={draft.status} onChange={(v) => setDraft({ ...draft, status: v })} options={[{ value: "draft", label: "Draft" }, { value: "published", label: "Published" }]} />
                            </div>
                            <div className="flex justify-end gap-2">
                                <Btn variant="ghost" onClick={() => { setCreating(false); setDraft(empty); }}>Cancel</Btn>
                                <Btn testId="evidence-save" onClick={create}><Save className="w-3.5 h-3.5" /> Save block</Btn>
                            </div>
                        </div>
                    ) : (
                        <div className="mt-4"><Btn testId="evidence-add" onClick={() => setCreating(true)}><Plus className="w-3.5 h-3.5" /> Add evidence block</Btn></div>
                    )}
                </div>
            ) : null}
        </div>
    );
}

function RelationshipsTab({ researchEntities, relationships, refresh }) {
    const [creating, setCreating] = useState(false);
    const empty = { from_entity_id: "", to_entity_id: "", relationship_type: "acquired", started_at: "", ended_at: "", source_url: "", notes_md: "", status: "published" };
    const [draft, setDraft] = useState(empty);
    const nameById = useMemo(() => Object.fromEntries(researchEntities.map((e) => [e.id, e.name])), [researchEntities]);

    const create = async () => {
        try {
            const payload = { ...draft };
            ["started_at", "ended_at", "source_url", "notes_md"].forEach((k) => { if (!payload[k]) delete payload[k]; });
            await api.post("/admin/entity-relationships", payload);
            toast.success("Relationship added.");
            setCreating(false); setDraft(empty); refresh();
        } catch (e) { toast.error(e?.response?.data?.detail || "Add failed."); }
    };
    const del = async (id) => {
        if (!window.confirm("Delete this relationship?")) return;
        try { await api.delete(`/admin/entity-relationships/${id}`); toast.success("Deleted."); refresh(); } catch (e) { toast.error("Delete failed."); }
    };

    return (
        <div>
            <SectionHeader
                title="Entity Relationships"
                subtitle="Corporate hierarchies, regulatory relationships, manufacturing connections."
                action={<Btn testId="relationship-new" onClick={() => setCreating(true)}><Plus className="w-3.5 h-3.5" /> New relationship</Btn>}
            />
            {creating ? (
                <div className="mb-5 bg-[#0B1220] border border-[#56CCF2]/30 rounded-md p-4 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Select label="From entity" value={draft.from_entity_id} onChange={(v) => setDraft({ ...draft, from_entity_id: v })} options={[{ value: "", label: "Choose…" }, ...researchEntities.map((e) => ({ value: e.id, label: `${e.name} (${e.entity_type})` }))]} testId="rel-from" />
                        <Select label="Relationship" value={draft.relationship_type} onChange={(v) => setDraft({ ...draft, relationship_type: v })} options={RELATIONSHIP_TYPES.map((t) => ({ value: t, label: t.replace(/_/g, " ") }))} testId="rel-type" />
                        <Select label="To entity" value={draft.to_entity_id} onChange={(v) => setDraft({ ...draft, to_entity_id: v })} options={[{ value: "", label: "Choose…" }, ...researchEntities.map((e) => ({ value: e.id, label: `${e.name} (${e.entity_type})` }))]} testId="rel-to" />
                        <Input label="Started" value={draft.started_at} onChange={(v) => setDraft({ ...draft, started_at: v })} placeholder="2009-10-15" />
                        <Input label="Ended (optional)" value={draft.ended_at} onChange={(v) => setDraft({ ...draft, ended_at: v })} />
                        <Input label="Source URL" value={draft.source_url} onChange={(v) => setDraft({ ...draft, source_url: v })} />
                    </div>
                    <Textarea label="Notes (markdown)" value={draft.notes_md} onChange={(v) => setDraft({ ...draft, notes_md: v })} rows={3} />
                    <div className="flex justify-end gap-2">
                        <Btn variant="ghost" onClick={() => { setCreating(false); setDraft(empty); }}>Cancel</Btn>
                        <Btn testId="rel-save" onClick={create}><Save className="w-3.5 h-3.5" /> Save</Btn>
                    </div>
                </div>
            ) : null}
            {relationships.length === 0 ? (
                <div className="text-[13px] text-[#64748B] py-12 text-center">No relationships yet.</div>
            ) : (
                <div className="space-y-2">
                    {relationships.map((r) => (
                        <div key={r.id} className="flex items-center justify-between bg-[#111827] border border-[#243044] rounded-md px-4 py-3" data-testid={`relationship-row-${r.id}`}>
                            <div className="text-sm text-white">
                                <span className="text-[#94A3B8]">{nameById[r.from_entity_id] || r.from_entity_id}</span>
                                <span className="mx-2 text-[#56CCF2] text-[11px] uppercase tracking-wider">→ {r.relationship_type.replace(/_/g, " ")} →</span>
                                <span className="text-[#94A3B8]">{nameById[r.to_entity_id] || r.to_entity_id}</span>
                                {r.started_at ? <span className="ml-3 text-[11px] text-[#64748B]">({r.started_at})</span> : null}
                            </div>
                            <Btn variant="danger" onClick={() => del(r.id)}><Trash2 className="w-3.5 h-3.5" /></Btn>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function CSVImportTab() {
    const [csv, setCsv] = useState("vaccine_slug,country,age_label,age_months,dose_number,total_doses,schedule_year\nmmr,UK,12 months,12,1,2,2026\nmmr,UK,3 years 4 months,40,2,2,2026\n");
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        setBusy(true); setResult(null);
        try {
            const r = await api.post("/admin/vaccine-schedules/import-csv", { csv });
            setResult(r.data);
            toast.success(`${r.data.created} rows imported.`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Import failed.");
        } finally { setBusy(false); }
    };

    return (
        <div>
            <SectionHeader title="CSV Schedule Import" subtitle="Bulk-seed schedule rows. Required columns: vaccine_slug, country, age_label, age_months, dose_number, total_doses, schedule_year." />
            <Textarea label="CSV content" value={csv} onChange={setCsv} rows={10} testId="csv-textarea" />
            <div className="mt-4"><Btn testId="csv-import" onClick={submit} disabled={busy}>{busy ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Importing…</> : <><Upload className="w-3.5 h-3.5" /> Import schedules</>}</Btn></div>
            {result ? (
                <div className="mt-5 bg-[#0B1220] border border-[#243044] rounded-md p-4" data-testid="csv-result">
                    <div className="text-sm text-white">Imported <span className="text-[#7FE0A6] font-semibold">{result.created}</span> of {result.total_rows} rows.</div>
                    {result.errors?.length ? (
                        <div className="mt-3 text-[12px] text-[#F87171]">
                            <div className="mb-1.5">Errors:</div>
                            <ul className="space-y-0.5">{result.errors.map((er, i) => <li key={i} className="font-mono">Row {er.row}: {er.error}</li>)}</ul>
                        </div>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}

// ---------- main page ----------

const TABS = [
    { id: "vaccines", label: "Vaccines", icon: ChevronRight },
    { id: "entities", label: "Research Entities", icon: Tag },
    { id: "schedules", label: "Schedules", icon: ChevronRight },
    { id: "evidence", label: "Evidence Blocks", icon: ChevronRight },
    { id: "relationships", label: "Relationships", icon: LinkIcon },
    { id: "csv", label: "CSV Import", icon: Upload },
];

export default function AdminVaccines() {
    const [tab, setTab] = useState("vaccines");
    const [vaccines, setVaccines] = useState([]);
    const [researchEntities, setResearchEntities] = useState([]);
    const [relationships, setRelationships] = useState([]);
    const [loading, setLoading] = useState(true);

    const refresh = async () => {
        setLoading(true);
        try {
            const [v, e, r] = await Promise.all([
                api.get("/admin/vaccines"),
                api.get("/admin/research-entities"),
                api.get("/admin/entity-relationships"),
            ]);
            setVaccines(v.data); setResearchEntities(e.data); setRelationships(r.data);
        } catch (err) {
            toast.error("Failed to load research explorer data.");
        } finally { setLoading(false); }
    };
    useEffect(() => { refresh(); }, []);

    return (
        <AdminLayout>
            <div className="px-8 py-8" data-testid="admin-vaccines-page">
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-[#56CCF2] font-medium mb-1">Research Explorer</div>
                        <h1 className="text-2xl font-bold text-white tracking-tight">Vaccines</h1>
                        <p className="text-xs text-[#64748B] mt-1">Editorial tooling for the Vaccination Research Explorer.</p>
                    </div>
                </div>
                <div className="flex flex-wrap gap-2 mb-7 border-b border-[#243044] pb-3">
                    {TABS.map((t) => (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            data-testid={`tab-${t.id}`}
                            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${tab === t.id ? "bg-[#2F80ED] text-white" : "bg-[#111827] text-[#94A3B8] hover:text-white border border-[#243044]"}`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
                {loading ? (
                    <div className="text-sm text-[#64748B] py-12 text-center"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</div>
                ) : (
                    <>
                        {tab === "vaccines" && <VaccinesTab vaccines={vaccines} refresh={refresh} researchEntities={researchEntities} />}
                        {tab === "entities" && <ResearchEntitiesTab entities={researchEntities} refresh={refresh} />}
                        {tab === "schedules" && <SchedulesTab vaccines={vaccines} refresh={refresh} />}
                        {tab === "evidence" && <EvidenceBlocksTab vaccines={vaccines} />}
                        {tab === "relationships" && <RelationshipsTab researchEntities={researchEntities} relationships={relationships} refresh={refresh} />}
                        {tab === "csv" && <CSVImportTab />}
                    </>
                )}
            </div>
        </AdminLayout>
    );
}
